import type { CaptureSettings } from '../types';

import logger from '@/lib/logging';
// Default capture settings
export const DEFAULT_CAPTURE_SETTINGS: CaptureSettings = {
  // AI Vision / MediaPipe
  modelQuality: 'full',
  processingMode: 'GPU',
  visibilityThreshold: 0.35,
  minPoseConfidence: 0.5,
  
  // Overlays & Grid
  gridType: '3x3',
  gridOpacity: 0.4,
  showGhostOutline: true,
  ghostOpacity: 0.3,
  showLevelIndicator: true,
  showPlumbLine: true,
  plumbLineStyle: 'dashed',
  
  // Auto-Capture
  enableAutoCapture: true,
  stabilityThreshold: 15,
  holdDuration: 2,
  countdownDuration: 6,
  
  // Image Enhancement
  autoBrightness: false,
  contrastBoost: 0,
  sharpen: false,
  
  // Image Transformations
  rotation: 0,
  perspectiveX: 0,
  perspectiveY: 0,
  tilt: 0,
  yaw: 0,
  zoom: 0,
  
  // Camera
  preferredResolution: '1280x720',
  mirrorFrontCamera: false,
  defaultFacingMode: 'user',
  
  // Display Settings
  landmarkSize: 1.6, // Default size multiplier
  landmarkFillColor: '#ffff00', // Yellow
  landmarkStrokeColor: '#000000', // Black
  skeletonLineWidth: 1.6, // Default line width multiplier
  skeletonLineColor: '#00ffff', // Cyan
  overlayLineWidth: 1.0, // Default overlay line width multiplier
  overlayLineColor: '#38bdf8', // Sky blue
  plumbLineColor: '#ffff00', // Yellow
  gridLineColor: '#ffffff', // White
  customLandmarkColor: '#ef4444', // Red (ASIS/PSIS pins)
  pelvisLineColor: '#ef4444', // Red (ASIS–PSIS line)
  pelvisLineWidth: 1.0,
  showPelvisLine: true,
  showPelvisLandmarks: true,
  landmarkSnap: false,
  landmarkSnapRadius: 20,
  plumblineSnap: false,
  plumblineSnapRadius: 8,
  toolbarPosition: 'bottom',
  settingsVersion: 2,
};

const STORAGE_KEY = 'posture-capture-settings';

/** Legacy display multipliers before thicker skeleton/landmark defaults (Mar 2026). */
const LEGACY_LANDMARK_SIZE = 1.0;
const LEGACY_SKELETON_LINE_WIDTH = 1.0;

/** Resolve a display/capture setting from partial overrides or {@link DEFAULT_CAPTURE_SETTINGS}. */
export function resolveCaptureDisplaySetting<K extends keyof CaptureSettings>(
  key: K,
  partial?: Partial<CaptureSettings>,
): CaptureSettings[K] {
  return partial?.[key] ?? DEFAULT_CAPTURE_SETTINGS[key];
}

function upgradeLegacyDisplayDefaults(parsed: Partial<CaptureSettings>): Partial<CaptureSettings> {
  const upgraded = { ...parsed };
  if (upgraded.landmarkSize === LEGACY_LANDMARK_SIZE) {
    upgraded.landmarkSize = DEFAULT_CAPTURE_SETTINGS.landmarkSize;
  }
  if (upgraded.skeletonLineWidth === LEGACY_SKELETON_LINE_WIDTH) {
    upgraded.skeletonLineWidth = DEFAULT_CAPTURE_SETTINGS.skeletonLineWidth;
  }

  // Migration to default landmarkSnap and plumblineSnap to false for existing users
  if (!upgraded.settingsVersion || upgraded.settingsVersion < 2) {
    upgraded.landmarkSnap = false;
    upgraded.plumblineSnap = false;
    upgraded.settingsVersion = 2;
  }

  return upgraded;
}

/**
 * Load capture settings from localStorage
 */
export function loadCaptureSettings(): CaptureSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<CaptureSettings>;
      const upgraded = upgradeLegacyDisplayDefaults(parsed);
      // Merge with defaults to ensure all fields exist
      const merged = { ...DEFAULT_CAPTURE_SETTINGS, ...upgraded };

      // Persist the upgraded settings if version was upgraded
      if (!parsed.settingsVersion || parsed.settingsVersion < 2) {
        saveCaptureSettings(merged);
      }

      return merged;
    }
  } catch (error) {
    logger.error('Error loading capture settings:', error);
  }
  return { ...DEFAULT_CAPTURE_SETTINGS };
}

/**
 * Save capture settings to localStorage
 */
export function saveCaptureSettings(settings: CaptureSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    logger.error('Error saving capture settings:', error);
  }
}

/**
 * Reset capture settings to defaults
 */
export function resetCaptureSettings(): CaptureSettings {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    logger.error('Error resetting capture settings:', error);
  }
  return { ...DEFAULT_CAPTURE_SETTINGS };
}

/**
 * Parse resolution string to width/height
 */
export function parseResolution(resolution: CaptureSettings['preferredResolution']): { width: number; height: number } {
  switch (resolution) {
    case '640x480':
      return { width: 640, height: 480 };
    case '1920x1080':
      return { width: 1920, height: 1080 };
    case '1280x720':
    default:
      return { width: 1280, height: 720 };
  }
}

/**
 * Get camera constraints based on settings
 */
export function getCameraConstraints(
  settings: CaptureSettings,
  deviceId?: string
): MediaStreamConstraints {
  const { width, height } = parseResolution(settings.preferredResolution);
  
  if (deviceId) {
    return {
      video: {
        deviceId: { exact: deviceId },
        width: { ideal: width },
        height: { ideal: height },
      }
    };
  }
  
  return {
    video: {
      facingMode: settings.defaultFacingMode,
      width: { ideal: width },
      height: { ideal: height },
    }
  };
}

